import React, { useMemo } from 'react';
import { FooterStyles } from './FooterStyles';
import {
  emailsData,
  marketResearch,
  menuMethodsItems,
  menuOtherItems,
  menuProductsItems,
  numbersData,
  partnerFooterIcons,
  shoppersPlatformUrls,
  socialMediasMenu,
} from '../../constants';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LightItUpTextIcon from '../../assets/icons/LightItUpTextIcon';
import FooterLogoIcon from '../../assets/icons/FooterLogoIcon';
import { useAppSelector } from '../../state/hooks';
import getTranslationsByLangOrEng from '../../utils/getTranslationsByLangOrLang';
import ButtonWithArrowOnHover from '../buttonWithArrowOnHover/ButtonWithArrowOnHover';

interface Props {
  colorTheme: 'darkTheme' | 'lightTheme' | 'greenTheme';
}

const iconColor = (colorTheme: 'darkTheme' | 'lightTheme' | 'greenTheme') => {
  switch (colorTheme) {
    case 'darkTheme':
      return {
        color: '#FFF',
        circle: 'rgba(255, 255, 255, 0.4)',
      };
    case 'greenTheme':
      return {
        color: '#FFF',
        circle: '#02BA95',
      };
    case 'lightTheme':
      return {
        color: '#000',
        circle: '#0000FF',
      };
    default:
      return {
        color: '#000',
        circle: '#0000FF',
      };
  }
};

const btnColor = (colorTheme: 'darkTheme' | 'lightTheme' | 'greenTheme') => {
  switch (colorTheme) {
    case 'darkTheme':
      return '#fff';
    case 'greenTheme':
      return '#fff';
    case 'lightTheme':
      return '#000';
    default:
      return '#fff';
  }
};

function Footer({ colorTheme }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { interfaceLanguage } = useAppSelector((state) => state.languages);

  return (
    <FooterStyles className={colorTheme} id="footer">
      <div className="container">
        <div className="footerContent">
          <div className="logoAndPartnersWrapper">
            <div className="logoContainer">
              <FooterLogoIcon />
              <LightItUpTextIcon
                color={colorTheme === 'lightTheme' ? '#000' : '#FFF'}
              />
            </div>
            <div className="partnersContainer">
              <span>
                {getTranslationsByLangOrEng(interfaceLanguage, 'footer_1')}
              </span>
              <div>
                {partnerFooterIcons.map((iconWithAlt, index) => (
                  <img
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    src={iconWithAlt.icon}
                    alt={iconWithAlt.alt}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="footerBtns">
            <ButtonWithArrowOnHover
              onClick={() => navigate(`/${interfaceLanguage}/shoppers`)}
              arrowColor={btnColor(colorTheme)}
              textColor={btnColor(colorTheme)}
              borderColor="#0000FF"
              borderOnHover="#0000FF"
              background="transparent"
              hoverBackground="#0000FF"
              hoverTextColor={btnColor(colorTheme)}
              text={getTranslationsByLangOrEng(
                interfaceLanguage,
                'footer_btn_1',
              )}
              className="footerBtn"
              withoutArrow
            />
            <ButtonWithArrowOnHover
              onClick={() => navigate(`/${interfaceLanguage}/4opinion`)}
              arrowColor={btnColor(colorTheme)}
              textColor={btnColor(colorTheme)}
              borderColor="#0000FF"
              borderOnHover="#0000FF"
              background="transparent"
              hoverBackground="#0000FF"
              hoverTextColor={btnColor(colorTheme)}
              text={getTranslationsByLangOrEng(
                interfaceLanguage,
                'footer_btn_2',
              )}
              className="footerBtn"
              withoutArrow
            />
          </div>
          <div className="navAndPrivacyPolicyWrapper">
            <div className="footerNavWrapper">
              <ul className="otherList">
                {menuOtherItems(interfaceLanguage).map((item, index) => {
                  if (item.shoppersPlatform) {
                    return (
                      <li
                        /* eslint-disable-next-line react/no-array-index-key */
                        key={index}
                        className={
                          item.url === '/' && pathname === '/'
                            ? 'active'
                            : pathname.startsWith(item.url) && item.url !== '/'
                              ? 'active'
                              : ''
                        }
                      >
                        <a
                          href={
                            shoppersPlatformUrls[interfaceLanguage]
                              ? shoppersPlatformUrls[interfaceLanguage]
                              : shoppersPlatformUrls.en
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.name}
                        </a>
                      </li>
                    );
                  }

                  if (item.toOtherSite) {
                    return (
                      <li
                        /* eslint-disable-next-line react/no-array-index-key */
                        key={index}
                        className={
                          item.url === '/' && pathname === '/'
                            ? 'active'
                            : pathname.startsWith(item.url) && item.url !== '/'
                              ? 'active'
                              : ''
                        }
                      >
                        <a
                          href={`${item.url}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.name}
                        </a>
                      </li>
                    );
                  }

                  return (
                    <li
                      /* eslint-disable-next-line react/no-array-index-key */
                      key={index}
                      className={
                        item.url === '/' && pathname === '/'
                          ? 'active'
                          : pathname.startsWith(item.url) && item.url !== '/'
                            ? 'active'
                            : ''
                      }
                    >
                      <Link to={`/${interfaceLanguage}${item.url}`}>
                        {item.name}
                      </Link>
                    </li>
                  );
                })}
              </ul>

              <div className="MethodsListWrapper">
                <Link className="footer-link" to={`/${interfaceLanguage}/products`}>
                  {getTranslationsByLangOrEng(interfaceLanguage, 'footer_2')}
                </Link>
                <ul>
                  {menuMethodsItems(interfaceLanguage).map((item, index) => (
                    <li
                      className={pathname.startsWith(item.url) ? 'active' : ''}
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <Link className="item-link" to={`/${interfaceLanguage}${item.url}`}>
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="MarketResearchListWrapper">
                <Link className="footer-link" to={`/${interfaceLanguage}/market-research`}>
                  {getTranslationsByLangOrEng(interfaceLanguage, 'footer_7')}
                </Link>
                <ul>
                  {marketResearch(interfaceLanguage).map((item, index) => (
                    <li
                      className={pathname.startsWith(item.url) ? 'active' : ''}
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                    >
                      <Link to={`/${interfaceLanguage}${item.url}`}>
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="ProductsListWrapper">
                <Link className="footer-link" to={`/${interfaceLanguage}/products`}>
                  {getTranslationsByLangOrEng(interfaceLanguage, 'footer_3')}
                </Link>
                <ul>
                  {menuProductsItems(interfaceLanguage).map((item, index) => {
                    if (
                      (item.onlyInLanguages
                        && item.onlyInLanguages.includes(interfaceLanguage))
                      || !item.onlyInLanguages
                    ) {
                      return item.otherSiteLink ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                          <a
                            target="_blank"
                            className={
                              pathname.startsWith(item.url) ? 'active' : ''
                            }
                            href={item.url}
                            rel="noreferrer"
                          >
                            {item.name}
                          </a>
                        </li>
                      ) : (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={index}>
                          <Link
                            className={
                              pathname.startsWith(item.url) ? 'active' : ''
                            }
                            to={`/${interfaceLanguage}${item.url}`}
                          >
                            {item.name}
                          </Link>
                        </li>
                      );
                    }

                    return null;
                  })}
                </ul>
              </div>
            </div>

            <div className="privacyPolicyWrapper">
              <div className="socialLinksWrapper">

                {(interfaceLanguage !== 'uk' && interfaceLanguage !== 'kk-kz' && interfaceLanguage !== 'ru-kz') && (
                  <div className="contacts">
                    <span>
                      {getTranslationsByLangOrEng(interfaceLanguage, 'footer_4')}
                    </span>

                    {numbersData(interfaceLanguage).map((number) => (
                      <a href={`tel:${number}`}>{number}</a>
                    ))}

                    <a href={`mailto:${emailsData(interfaceLanguage)}`}>
                      {emailsData(interfaceLanguage)}
                    </a>
                  </div>
                )}

                {(interfaceLanguage === 'kk-kz' || interfaceLanguage === 'ru-kz') && (
                  <div className="contacts">
                    <span>
                      {getTranslationsByLangOrEng(interfaceLanguage, 'footer_9')}
                    </span>

                    <a href="tel:+77076716116">+77076716116</a>
                    <a className="whatsapp" href="https://wa.me/77076716116" target="_blank" rel="noreferrer">
                      <svg height="15px" width="15px" viewBox="0 0 58 58">
                        <path fill="#2cb742" d="M0,58l4.988-14.963C2.457,38.78,1,33.812,1,28.5C1,12.76,13.76,0,29.5,0S58,12.76,58,28.5 S45.24,57,29.5,57c-4.789,0-9.299-1.187-13.26-3.273L0,58z" />
                        <path fill="#FFFFFF" d="M47.683,37.985c-1.316-2.487-6.169-5.331-6.169-5.331c-1.098-0.626-2.423-0.696-3.049,0.42 c0,0-1.577,1.891-1.978,2.163c-1.832,1.241-3.529,1.193-5.242-0.52l-3.981-3.981l-3.981-3.981c-1.713-1.713-1.761-3.41-0.52-5.242 c0.272-0.401,2.163-1.978,2.163-1.978c1.116-0.627,1.046-1.951,0.42-3.049c0,0-2.844-4.853-5.331-6.169 c-1.058-0.56-2.357-0.364-3.203,0.482l-1.758,1.758c-5.577,5.577-2.831,11.873,2.746,17.45l5.097,5.097l5.097,5.097 c5.577,5.577,11.873,8.323,17.45,2.746l1.758-1.758C48.048,40.341,48.243,39.042,47.683,37.985z" />
                      </svg>
                      <span>WhatsApp</span>
                    </a>
                    <a href="mailto:info@4service.kz">
                      info@4service.kz
                    </a>

                    <span>
                      {getTranslationsByLangOrEng(interfaceLanguage, 'footer_10')}
                    </span>

                    <a href="mailto:sd@4service.support">
                      sd@4service.support
                    </a>
                    <a href="tel:+7 7273122636">
                      {getTranslationsByLangOrEng(interfaceLanguage, 'contacts_20')} +7 727 312 26 36
                    </a>
                    <a href="https://t.me/kz4servicesupport_bot">
                      {getTranslationsByLangOrEng(interfaceLanguage, 'contacts_21')} @kz4servicesupport_bot
                    </a>
                  </div>
                )}

                {(interfaceLanguage === 'uk') && (
                  <div className="contacts">
                    <span>
                      {getTranslationsByLangOrEng(interfaceLanguage, 'footer_9')}
                    </span>

                    <a href="tel:+380673827810">+380673827810</a>
                    <a href="mailto:info@4service.ua">
                      info@4service.ua
                    </a>

                    <span>
                      {getTranslationsByLangOrEng(interfaceLanguage, 'footer_10')}
                    </span>

                    <a href="mailto:ua@4service.support">
                      ua@4service.support
                    </a>
                    <a href="http://t.me/ua4servicesupport_bot">
                      {getTranslationsByLangOrEng(interfaceLanguage, 'contacts_21')} @ua4servicesupport_bot
                    </a>
                  </div>
                )}

                <div className="socialLinksContainer">
                  {socialMediasMenu.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <a aria-label="link" href={item.url} className="iconWrapper" key={index}>
                        <Icon
                          color={iconColor(colorTheme).color}
                          circleColor={iconColor(colorTheme).circle}
                          width={29}
                          height={29}
                        />
                      </a>
                    );
                  })}
                </div>
              </div>

              <div className="privacyPolicyContainer">
                <Link to={`/${interfaceLanguage}/privacy-policy`}>
                  <span>
                    {getTranslationsByLangOrEng(interfaceLanguage, 'footer_6')}
                  </span>
                </Link>
                <Link to={`/${interfaceLanguage}/privacy-notice`}>
                  <span>
                    {getTranslationsByLangOrEng(interfaceLanguage, 'footer_8')}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FooterStyles>
  );
}

export default Footer;
